import {
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    inject,
    Inject,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    CONCURRENT_USAGE_ERROR_CODES,
    ConfigConstants,
    CONNECTION_ERROR_CODES,
    ConnectivityService,
    DateModel,
    DateModelOptions,
    isDefined,
    LoadingScreenComponent,
    LoadingScreenModel,
    ModalClass,
    ModelOptionsLoaderService,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationStatusInstanceToken,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TIMESHEET_KEYS,
    TimesheetAndExpensesP58Model,
    isEowEntry,
    isManualEntry,
    isNormalEntry,
    isSowEntry,
    TimesheetModel,
    TimesheetModelFromJson,
    TimesheetNotificationModelFromJson,
    TimesheetService,
    UserMasterInstanceToken,
    UserMasterInterface,
    WindowService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesInterface,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, from, iif, Observable, of, Subject, switchMap, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, take, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faCheckCircle, faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons';
import { TimesheetCanDeactivateHost } from '../../timesheet-can-deactivate.interface';

const CLEAR_STATUS_INTERVAL = 3000;

@Component({
    selector: 'pace-timesheet-and-expenses',
    templateUrl: './timesheet-and-expenses.component.html',
    styleUrls: ['./timesheet-and-expenses.component.scss'],
    standalone: false,
})
export class TimesheetAndExpensesComponent implements OnInit, TimesheetCanDeactivateHost {
    private readonly notificationStatusService = inject(NotificationStatusInstanceToken);

    public model: TimesheetAndExpensesP58Model;

    public timesheetAndExpensesForm: FormGroup;
    public timesheetFormChildren: FormArray;
    public expensesFormChildren: FormArray;

    public timesheetDatePicker: DateModel;
    public isSubmitting = false;
    private enableTimesheetEmail = false;
    private warningWorkHoursExceededAcknowledged = false;
    private warningEowSetInFutureAcknowledged = false;
    private lastCheckedEowDateTime?: Moment;
    private readonly window: Window;
    public isOnline$ = this.connectivityService.isOnline$;

    public strings: {
        timesheetHeading: string;
        expensesHeading: string;
        invalidTimesheetMessage: string;
        submittedTimesheetMessage: string;
        noSubmitOnCurrentDayMessage: string;
        downloadButtonLabel: string;
        uploadButtonLabel: string;
    };

    protected readonly faLoading = faSync;
    protected readonly faCheckCircle = faCheckCircle;
    protected readonly faExclamationTriangle = faExclamationTriangle;

    public timesheetSyncStatus = ConfigConstants.SYNCING_STATUS;

    public uploadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.timesheetSyncStatus.IDLE);
    public downloadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.timesheetSyncStatus.IDLE);
    public downloadInProgress = computed(() => this.downloadStatus() === this.timesheetSyncStatus.LOADING);

    private resetStatusChangeSubject: Subject<void> = new Subject<void>();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        private readonly formBuilder: FormBuilder,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        private readonly timesheetService: TimesheetService,
        private readonly appNotificationService: AppNotificationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly cd: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
        private readonly connectivityService: ConnectivityService,
        private readonly modelOptionsLoader: ModelOptionsLoaderService,
        private readonly windowService: WindowService,
    ) {
        this.strings = {
            timesheetHeading: <string>translate.instant('TIMESHEET_SHEET_TAB_TITLE'),
            expensesHeading: <string>translate.instant('TIMESHEET_EXPENSES_TAB_TITLE'),
            invalidTimesheetMessage: <string>translate.instant('TIMESHEET_INVALID_MESSAGE'),
            submittedTimesheetMessage: <string>translate.instant('TIMESHEET_SUBMITTED_MESSAGE'),
            noSubmitOnCurrentDayMessage: <string>translate.instant('TIMESHEET_NO_SUBMIT_ON_CURRENT_DAY_MESSAGE'),
            downloadButtonLabel: <string>translate.instant('TIMESHEET_DOWNLOAD_TIMESHEET_TITLE'),
            uploadButtonLabel: <string>translate.instant('TIMESHEET_UPLOAD_TIMESHEET_TITLE'),
        };

        this.window = this.windowService.nativeWindow;

        /*
         The current timesheet needs to be subscribed to, but updates should only be applied, if the user is working on
         the newest version of their timesheet. If a different device uploads a new version and the change can potentially
         overwrite existing edits, then the user needs to be prompted, so they can decide which version to keep and which
         version to throw out.
        */
        const selectCurrentTimesheet$ = this.timesheetAndExpensesService.selectCurrentTimesheet$().pipe(
            filter(isDefined),
            mergeMap((timesheet) =>
                // Ensure that the flag `isConfirmationSubmitted` is correct, by checking the state of completed notifications
                // on the notification status service
                this.notificationStatusService.loadRecentlyCompletedNotifications().pipe(
                    map((completedNotificationState) => {
                        let hasChanged = false;
                        const completedNotificationKeys = Object.keys(
                            completedNotificationState.completedNotifications,
                        );

                        const updatedTimesheetNotifications = timesheet.notifications.map((notification) => {
                            if (notification.isConfirmationSubmitted) {
                                return notification;
                            }
                            const isConfirmationAlreadyCompleted = completedNotificationKeys.includes(
                                notification.notificationId.toString(),
                            );
                            if (!isConfirmationAlreadyCompleted) {
                                return notification;
                            }
                            hasChanged = true;
                            this.log.error(
                                `TimesheetAndExpensesComponent: selectCurrentTimesheet$: a notification was falsely hidden on the timesheet. The flag 'isConfirmationSubmitted has been updated to true. NotificationId: ${notification.notificationId.toString()}`,
                            );
                            const newNotification = new TimesheetNotificationModelFromJson(
                                JSON.parse(JSON.stringify(notification)),
                            );
                            newNotification.isConfirmationSubmitted = isConfirmationAlreadyCompleted;
                            return newNotification;
                        });

                        if (hasChanged) {
                            const newTimesheet = new TimesheetModelFromJson(
                                JSON.parse(JSON.stringify(timesheet)),
                                TIMESHEET_KEYS.CURRENT_TIMESHEET,
                            );
                            newTimesheet.notifications = updatedTimesheetNotifications;
                            return { timesheet: newTimesheet, updateTimesheetUI: false };
                        }
                        return { timesheet, updateTimesheetUI: true };
                    }),
                ),
            ),
            mergeMap((timesheetWithUpdateFlag) => this.onTimesheetUpdateStopEventAfterWriting(timesheetWithUpdateFlag)),
        );

        // setup timesheet and expenses data subscription
        combineLatest([selectCurrentTimesheet$, this.timesheetAndExpensesService.selectSubmittedTimesheet$()])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([currentTimesheet, submittedTimesheet]) => {
                this.timesheetService.isAutoSaveAllowedForNextChange = false;
                this.model = new TimesheetAndExpensesP58Model(
                    this.log,
                    this.translate,
                    this.modelOptionsLoader,
                    this.userMasterService.getSelectedUser().frontId,
                    this.configService.getDisplayTimesheetAddressOptions(),
                    this.clientManager.getTimeFormat(),
                    currentTimesheet,
                    submittedTimesheet,
                );
                this.log.debug(`TimesheetAndExpensesComponent: recreated TimesheetAndExpensesModel. (${Date.now()})`);

                // With the arrival of new timesheet data we are definitely not downloading anymore
                this.downloadStatus.set(this.timesheetSyncStatus.SUCCESS);

                // TODO: Check if we still need this
                // TODO: define model as observable and use async pipe in markup, and obs$ logic in rest of this file
                this.cd.markForCheck();
            });
    }

    public ngOnInit(): void {
        // The timesheet model is recreated automatically every time the timesheet component is displayed to always show the
        // latest time recording entries.
        this.downloadTimesheetFromConcurrencyStore();

        this.enableTimesheetEmail = this.configService.getEnableTimesheetEmail();
        const now = moment();
        const timesheetDate = <moment.Moment>this.activatedRoute.snapshot.data.date;
        this.timesheetDatePicker = new DateModel(
            'timesheet',
            timesheetDate ? timesheetDate.clone() : now.clone(),
            new DateModelOptions({ maxValue: now.clone() }),
        );

        this.timesheetFormChildren = this.formBuilder.array([]);
        this.expensesFormChildren = this.formBuilder.array([]);
        this.timesheetAndExpensesForm = this.formBuilder.group({
            timesheet: this.timesheetFormChildren,
            expenses: this.expensesFormChildren,
        });

        // This subject resets the button indicators after a timer interval CLEAR_STATUS_INTERVAL from successful download or error to idle.
        this.resetStatusChangeSubject
            .asObservable()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => timer(CLEAR_STATUS_INTERVAL).pipe(take(1))),
                tap(() => {
                    this.uploadStatus.set(this.timesheetSyncStatus.IDLE);
                    this.downloadStatus.set(this.timesheetSyncStatus.IDLE);
                }),
            )
            .subscribe();
    }

    public isTimesheetSendDisabled(): boolean {
        return (
            this.timesheetAndExpensesForm.invalid ||
            this.model?.readonly ||
            this.isSubmitting ||
            this.model?.timesheetEntries.value.length === 0
        );
    }

    public isExpenseFormInvalid(): boolean {
        return this.expensesFormChildren ? this.expensesFormChildren.invalid : false;
    }

    public isTimesheetFormInvalid(): boolean {
        return this.timesheetFormChildren ? this.timesheetFormChildren.invalid : false;
    }

    public submit(): void {
        this.log.debug(`${this.constructor.name}: submit timesheet`, { isSubmitting: this.isSubmitting });
        // make sure that submit can only be run once
        if (this.isSubmitting) {
            return;
        }

        // Show a warning if the end of work is in the future. If it is, show a warning popup, but don't block the user.
        // Same pattern as below -> work hours exceeds 10 hours
        const timesheetEowEntry = this.model.timesheetEntries.value.find((entry) => isEowEntry(entry));

        if (
            this.warningEowSetInFutureAcknowledged &&
            timesheetEowEntry &&
            !this.lastCheckedEowDateTime?.isSame(timesheetEowEntry?.endTime.value)
        ) {
            this.warningEowSetInFutureAcknowledged = false;
        }

        this.lastCheckedEowDateTime = moment(timesheetEowEntry?.endTime.value);

        if (timesheetEowEntry && timesheetEowEntry.endTime.value.isAfter() && !this.warningEowSetInFutureAcknowledged) {
            const warningEoWModal = this.modalService.show(YesNoModalDialogComponent, {
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_TITLE'),
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_BODY'),
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_OK_ACTION'),
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_NO_ACTION'),
                    ),
                },
                backdrop: 'static',
                keyboard: false,
                ariaLabelledBy: 'yes-no-modal-title',
            });

            warningEoWModal.content?.modalResult
                .asObservable()
                .pipe(
                    take(1),
                    tap((resultAction) => {
                        this.warningEowSetInFutureAcknowledged = resultAction;
                    }),
                )
                .subscribe();

            return;
        }

        // Show warning if total work hours is more than 10hours
        if (
            this.configService.getMaxWorkingHoursInTimesheet() > -1 &&
            this.isExceedingWorkHours(this.model) &&
            !this.warningWorkHoursExceededAcknowledged
        ) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('TIMESHEET_TIME_HOURS_EXCEEDING_TITLE'),
                <string>this.translate.instant('TIMESHEET_TIME_HOURS_EXCEEDING_LABEL', {
                    workhour: this.configService.getMaxWorkingHoursInTimesheet(),
                }),
                () => {
                    this.warningWorkHoursExceededAcknowledged = true;
                },
            );
            return;
        }

        this.log.debug(
            `TimesheetAndExpensesComponent: submit: submitting the timesheet to the gateway. (${Date.now()})`,
        );
        this.isSubmitting = true;

        // open progress modal while submitting
        const loadingScreenModel = new LoadingScreenModel(<string>this.translate.instant('TIMESHEET_SUBMIT_MESSAGE'));
        const submitModalInstance = this.modalService.show(LoadingScreenComponent, {
            class: 'modal-sm',
            initialState: {
                model: loadingScreenModel,
            },
            ignoreBackdropClick: true,
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        let inProgressWarningText = '';

        this.log.debug(`${this.constructor.name}: submit: start the actual submission`, {
            isSubmitting: this.isSubmitting,
        });

        loadingScreenModel.loadingScreenDisplayed$
            .pipe(
                mergeMap(() => {
                    this.log.debug(`${this.constructor.name}: synchronising notication status list`, {
                        isSubmitting: this.isSubmitting,
                    });
                    return this.notificationStatusService.syncNotificationStatusList().pipe(
                        mergeMap(() => {
                            // P58: Display error message if there are unconfirmed notifications in progress since we only allow to submit the
                            //      timesheet once
                            const notificationInProgress =
                                this.notificationService.getNotificationInProgress()?.identifier;
                            const notificationToBeSigned =
                                this.notificationService.getNotificationToBeSigned()?.identifier;

                            const syncedNotificationStatusInProgressOrToBeSigned =
                                this.notificationStatusService.getNotificationByStatusInProgressOrToBeSigned()
                                    ?.notificationId;
                            const syncedNotificationStatusIsPaused =
                                this.notificationStatusService.getNotificationStatusIsPaused()?.notificationId;

                            const notificationForWarning =
                                notificationInProgress ||
                                notificationToBeSigned ||
                                syncedNotificationStatusInProgressOrToBeSigned ||
                                syncedNotificationStatusIsPaused;

                            // check if there are any unfinished (=in progress or to be signed or paused) notifications
                            if (notificationForWarning) {
                                inProgressWarningText = <string>this.translate.instant(
                                    'TIMESHEET_NOTIFICATION_IN_PROGRESS_ERROR_LABEL',
                                    {
                                        qmnum: notificationForWarning.qmNum,
                                        manum: notificationForWarning.maNum,
                                    },
                                );
                                return throwError(() => CONCURRENT_USAGE_ERROR_CODES.NOTIFICATIONS_OUTDATED);
                            }
                            return of(undefined);
                        }),
                    );
                }),
                // send the whole timesheet to the offline plugin
                mergeMap(() => {
                    this.log.debug(
                        `${this.constructor.name}: submit: triggering submission through timesheetAndExpensesService`,
                    );
                    return this.timesheetAndExpensesService.submit(this.model);
                }),
                mergeMap(() => from(this.router.navigate([ConfigConstants.routeUrlDashboard]))),
                mergeMap(() => (this.enableTimesheetEmail ? this.generateTimesheetAndExpensesEmail() : of(undefined))),
                catchError((error) => {
                    this.log.error(
                        'TimesheetAndExpensesComponent: submit: Error while submitting timesheet: ' +
                            JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    switch (error) {
                        case CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE'),
                                <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY'),
                            );
                            break;
                        case CONCURRENT_USAGE_ERROR_CODES.TIMESHEET_OUTDATED:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('TIMESHEET_REMOTE_UPDATE_REQUIRED_TITLE'),
                                <string>this.translate.instant('TIMESHEET_REMOTE_UPDATE_REQUIRED_BODY'),
                            );
                            break;
                        case CONCURRENT_USAGE_ERROR_CODES.NOTIFICATIONS_OUTDATED:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('TIMESHEET_NOTIFICATION_IN_PROGRESS_ERROR_TITLE'),
                                inProgressWarningText,
                            );
                            break;
                        default:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('TIMESHEET_REQUEST_ERROR_TITLE'),
                                <string>this.translate.instant('TIMESHEET_REQUEST_ERROR_BODY'),
                            );
                            break;
                    }
                    return of(undefined);
                }),
                finalize(() => {
                    this.isSubmitting = false;
                    submitModalInstance.hide();
                }),
            )
            .subscribe();
    }

    // Trigger a manual timesheet download, for example when the user knows that they have been working on a different
    // device
    public downloadTimesheetFromConcurrencyStore(): void {
        this.log.debug(
            `TimesheetAndExpensesComponent: downloadTimesheetFromConcurrencyStore: download Timesheet (${Date.now()})`,
        );
        this.downloadStatus.set(this.timesheetSyncStatus.LOADING);
        combineLatest([
            this.timesheetAndExpensesService.selectCurrentTimesheet$(),
            this.timesheetAndExpensesService.selectSubmittedTimesheet$(),
        ])
            .pipe(
                take(1),
                tap(() => this.downloadStatus.set(this.timesheetSyncStatus.SUCCESS)),
                catchError((error) => {
                    this.downloadStatus.set(this.timesheetSyncStatus.ERROR);
                    return throwError(() => error);
                }),
                finalize(() => this.clearStatus()),
            )
            .subscribe();
    }

    // TODO: Remove; now every timesheet change gets synced to IndexedDB and triggers a concurrency store upload automatically
    public saveAndUploadTimesheetToConcurrencyStore(): void {
        this.uploadStatus.set(this.timesheetSyncStatus.LOADING);
        this.log.debug(
            `TimesheetAndExpensesComponent: saveAndUploadTimesheetToConcurrencyStore: writing the component's model to the concurrency store and indexedDb and updating the service's currentTimesheet. (${Date.now()})`,
        );
        this.timesheetAndExpensesService
            .saveTimesheetFormModel(this.model)
            .pipe(
                take(1),
                tap((timesheet) => {
                    if (timesheet) {
                        this.uploadStatus.set(this.timesheetSyncStatus.SUCCESS);
                    } else {
                        this.uploadStatus.set(this.timesheetSyncStatus.ERROR);
                    }
                }),
                catchError((error) => {
                    this.uploadStatus.set(this.timesheetSyncStatus.ERROR);
                    return throwError(() => error);
                }),
                finalize(() => this.clearStatus()),
            )
            .subscribe();
    }

    public triggerAutoSaveTimesheet(): void {
        this.saveBeforeDeactivate().pipe(take(1)).subscribe();
    }

    /** {@link TimesheetCanDeactivateHost}: persist the form model when leaving the route. */
    public saveBeforeDeactivate(): Observable<TimesheetModel | void> {
        return this.timesheetAndExpensesService.saveTimesheetFormModel(this.model);
    }

    private generateTimesheetAndExpensesEmail(): Observable<TimesheetModel> {
        const curlyBraceOpenRegExp = new RegExp('{', 'g');
        const curlyBraceCloseRegExp = new RegExp('}', 'g');
        const commaRegExp = new RegExp(',', 'g');

        const emailId = this.userMasterService.getSelectedUserModel().getEmailId();

        return this.timesheetAndExpensesService.selectSubmittedTimesheet$().pipe(
            take(1),
            tap((timesheet) => {
                const startDateString = timesheet.startOfWork.format(ConfigConstants.formatPatternDateGeneric);
                const startTimeString = timesheet.startOfWork.format(ConfigConstants.formatPatternTimeGeneric);
                const endDateString = timesheet.getEndOfWork().format(ConfigConstants.formatPatternDateGeneric);
                const endTimeString = timesheet.getEndOfWork().format(ConfigConstants.formatPatternTimeGeneric);

                const emailSubject = <string>this.translate.instant('TIMESHEET_EMAIL_SUBJECT', {
                    startDate: startDateString,
                    startTime: startTimeString,
                    endDate: endDateString,
                    endTime: endTimeString,
                });
                const emailBody = JSON.stringify(this.model.getOutModel())
                    .replace(curlyBraceOpenRegExp, '{\n')
                    .replace(curlyBraceCloseRegExp, '\n}')
                    .replace(commaRegExp, ',\n');

                const newWindow = this.window.open(
                    `mailTo:${emailId}?` + `subject=${emailSubject}` + `&body=${encodeURIComponent(emailBody)}`,
                    '_blank',
                );
                if (newWindow) {
                    newWindow.opener = null;
                }
            }),
        );
    }

    private clearStatus(): void {
        this.resetStatusChangeSubject.next();
    }

    public timeInspect(): void {
        this.router.navigate(['main', 'pace-spector']).catch(console.error);
    }

    /**
     * This method is to determine whether the working hours in timesheet is greater than total working hours (10)
     * EOW - SOW - (Pause + Break)
     *
     * @param {TimesheetAndExpensesP58Model} timesheetAndExpenses
     * @returns true if the working hours are greater than total working hours
     * @throws Error if the service is not initialized
     */
    private isExceedingWorkHours(timesheetAndExpenses: TimesheetAndExpensesP58Model): boolean {
        const startOfWork = timesheetAndExpenses.timesheetEntries.value.find((entry) => isSowEntry(entry));
        const endOfWork = timesheetAndExpenses.timesheetEntries.value.find((entry) => isEowEntry(entry));
        if (startOfWork && startOfWork.startDate && endOfWork && endOfWork.endDate) {
            // Calculate for break and pause times only if total timesheet hours is more than configService.getMaxTotalWorkingHours()
            const totalWorkingTime = moment.duration(endOfWork.endDate.value.diff(startOfWork.startDate.value));
            if (totalWorkingTime.asHours() > this.configService.getMaxWorkingHoursInTimesheet()) {
                let pauseTime = moment.duration(0);
                let breakTime = moment.duration(0);
                timesheetAndExpenses.timesheetEntries.value.map((entry) => {
                    // entry.travelType is available only when we "add time slot" not when we "add travel back"
                    if (
                        isManualEntry(entry) &&
                        !entry.travelType &&
                        this.configService.getTimesheetPauseCodes(entry.taskTypeDropdown.selectedEntry.value)
                    ) {
                        pauseTime = pauseTime.add(moment.duration(entry.endDate.value.diff(entry.startDate.value)));
                    }
                    if (isNormalEntry(entry) && entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.BREAK) {
                        breakTime = breakTime.add(moment.duration(entry.endDate.value.diff(entry.startDate.value)));
                    }
                });
                // subtract the pause and breaks to get actual working hours
                const workingHours = totalWorkingTime.subtract(pauseTime).subtract(breakTime).asHours();
                return workingHours > this.configService.getMaxWorkingHoursInTimesheet();
            }
        } else {
            this.log.error(
                `TimesheetAndExpensesService: isExceedingWorkHours: SOW: ${startOfWork} and/or EOW: ${endOfWork} is undefined`,
            );
        }

        return false;
    }

    /**
     * If the timesheet was changed, write the changes back to IndexedDb and stop the current event on the pipeline.
     * The write process to IndexedDb must trigger a new event via liveQuery.
     *
     * @returns TimesheetModel
     * @param timesheetWithUpdateFlag contains timesheet and an update flag
     * @param timesheetWithUpdateFlag.timesheet the last emitted value of the timesheet
     * @param timesheetWithUpdateFlag.updateTimesheetUI boolean that indicates whether the UI should be updated with this event's timesheet values
     * @private
     */
    private onTimesheetUpdateStopEventAfterWriting(timesheetWithUpdateFlag: {
        timesheet: TimesheetModel;
        updateTimesheetUI: boolean;
    }): Observable<TimesheetModel> {
        return iif(
            () => timesheetWithUpdateFlag.updateTimesheetUI,
            of(timesheetWithUpdateFlag),
            this.timesheetAndExpensesService.saveCurrentTimesheet$(timesheetWithUpdateFlag.timesheet).pipe(
                map((ts) => ({
                    timesheet: ts,
                    updateTimesheetUI: timesheetWithUpdateFlag.updateTimesheetUI,
                })),
            ),
        ).pipe(
            filter((result) => result.updateTimesheetUI),
            map((result) => result.timesheet),
        );
    }
}
